import React from "react";
import type { ColumnsType } from "antd/es/table";

// Two-level norm-group tree for the Norms Master lists (Manpower + Machinery).
//
// Level 1 is the BOQ head (B3 Underground Excavation), level 2 the authored
// group (B3.2 Excavation of Hydraulic tunnels). BOTH levels are needed: every
// machinery norm on a tunnelling project sits under B3, so a single level would
// render one header over 111 rows — the flat list with extra chrome.
//
// Where a group has only ONE sub-group the sub-level is skipped rather than
// rendering a pointless single child. Same rule as the 3M Deviation Summary
// register, so the two screens read alike.

export const UNGROUPED_LABEL = "Ungrouped";

export interface NormTreeRow {
  /** Synthetic on group rows; the norm's own id on leaves. */
  rowKey: string;
  isGroupRow?: boolean;
  groupLabel?: string;
  /** Leaf count beneath this group, at any depth. */
  groupCount?: number;
  /** Number of sub-groups (level-1 rows only), for the meta line. */
  subGroupCount?: number;
  depth?: number;
  children?: NormTreeRow[];
  [key: string]: any;
}

const label = (code?: string, name?: string) =>
  code ? `${code}${name ? ` — ${name}` : ""}` : "";

/**
 * Group a flat page of norms into the two-level tree.
 * `idField` is the master's primary key, used to key leaf rows.
 */
export const buildNormGroupTree = (
  rows: any[],
  idField: string,
): NormTreeRow[] => {
  // Plain objects + Array.from rather than Map iteration: this tsconfig
  // targets below ES2015, so `for...of` over a Map needs downlevelIteration.
  const byLevel1 = new Map<string, Map<string, any[]>>();

  (rows || []).forEach((row) => {
    const l1 = label(row?.level1Code, row?.level1Name) || UNGROUPED_LABEL;
    const l2 = label(row?.groupCode, row?.groupName) || l1;
    if (!byLevel1.has(l1)) byLevel1.set(l1, new Map());
    const subs = byLevel1.get(l1)!;
    if (!subs.has(l2)) subs.set(l2, []);
    subs.get(l2)!.push(row);
  });

  const leaf = (row: any, depth: number): NormTreeRow => ({
    ...row,
    rowKey: `n-${row?.[idField]}`,
    depth,
  });

  const out: NormTreeRow[] = [];
  Array.from(byLevel1.entries()).forEach(([l1, subs]) => {
    const subEntries = Array.from(subs.entries());
    const total = subEntries.reduce((n, [, list]) => n + list.length, 0);

    // One sub-group adds a level of indirection and no information.
    const flattenSubs = subEntries.length < 2;
    const children: NormTreeRow[] = flattenSubs
      ? subEntries[0][1].map((row: any) => leaf(row, 1))
      : subEntries.map(([l2, list]) => ({
          rowKey: `g2-${l1}-${l2}`,
          isGroupRow: true,
          groupLabel: l2,
          groupCount: list.length,
          depth: 1,
          children: list.map((row: any) => leaf(row, 2)),
        }));

    out.push({
      rowKey: `g1-${l1}`,
      isGroupRow: true,
      groupLabel: l1,
      groupCount: total,
      subGroupCount: flattenSubs ? 0 : subEntries.length,
      depth: 0,
      children,
    });
  });
  return out;
};

/** Every group row's key, for the expand-all control. */
export const allGroupKeys = (rows: NormTreeRow[]): string[] => {
  const keys: string[] = [];
  const walk = (row: NormTreeRow) => {
    if (!row.isGroupRow) return;
    keys.push(row.rowKey);
    (row.children || []).forEach(walk);
  };
  rows.forEach(walk);
  return keys;
};

/**
 * Wrap a page's columns for tree mode.
 *
 * A group row carries no norm fields, so its first cell spans the whole table
 * (`colSpan`) and every other cell is suppressed with `colSpan: 0` — antd's way
 * of merging a row. Without the suppression the group's label would sit in
 * column one and the remaining columns would render empty borders.
 */
export const withGroupRows = (
  columns: ColumnsType<any>,
): ColumnsType<any> =>
  columns.map((column, index) => {
    const original: any = column;
    return {
      ...original,
      onCell: (record: NormTreeRow) => {
        const base =
          typeof original.onCell === "function" && !record?.isGroupRow
            ? original.onCell(record)
            : {};
        if (!record?.isGroupRow) return base;
        return { ...base, colSpan: index === 0 ? columns.length : 0 };
      },
      render: (value: any, record: NormTreeRow, rowIndex: number) => {
        if (record?.isGroupRow) {
          if (index !== 0) return null;
          const meta = [
            `${record.groupCount} norm${record.groupCount === 1 ? "" : "s"}`,
            record.subGroupCount
              ? `${record.subGroupCount} sub-group${record.subGroupCount === 1 ? "" : "s"}`
              : "",
          ]
            .filter(Boolean)
            .join(" · ");
          return (
            <span>
              <b style={{ color: "#022c57", fontWeight: record.depth === 0 ? 600 : 500 }}>
                {record.groupLabel}
              </b>
              <span style={{ fontSize: 11.5, color: "#8094ae", marginLeft: 8 }}>
                {meta}
              </span>
            </span>
          );
        }
        return original.render
          ? original.render(value, record, rowIndex)
          : value;
      },
    };
  });
